import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Archive, Calendar, TrendingUp, Users } from 'lucide-react';
import { toast } from 'sonner';

interface ArchivedSeason {
  id: string;
  season_name: string;
  season_year: number;
  start_date: string;
  end_date: string;
  entity_type: string;
  total_games: number;
  total_revenue: number;
  total_participants: number;
  summary_stats: any;
  archived_at: string;
}

interface SeasonArchiveManagerProps {
  entityId: string;
  entityType: 'team' | 'venue' | 'coach';
}

export const SeasonArchiveManager = ({ entityId, entityType }: SeasonArchiveManagerProps) => {
  const [archivedSeasons, setArchivedSeasons] = useState<ArchivedSeason[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArchivedSeasons();
  }, [entityId, entityType]);

  const loadArchivedSeasons = async () => {
    try {
      const { data, error } = await supabase
        .from('archived_seasons')
        .select('*')
        .eq('entity_id', entityId)
        .eq('entity_type', entityType)
        .order('season_year', { ascending: false });

      if (error) throw error;
      setArchivedSeasons(data || []);
    } catch (error) {
      console.error('Error loading archived seasons:', error);
      toast.error('Failed to load archived seasons');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground">Loading archived seasons...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Season Archives</h3>
          <p className="text-sm text-muted-foreground">
            View historical performance data from past seasons
          </p>
        </div>
        <Badge variant="secondary">
          <Archive className="w-3 h-3 mr-1" />
          {archivedSeasons.length} Seasons
        </Badge>
      </div>

      {archivedSeasons.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <Archive className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No archived seasons yet</p>
            <p className="text-sm text-muted-foreground mt-2">
              Past season data will appear here automatically
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {archivedSeasons.map((season) => (
            <Card key={season.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{season.season_name}</CardTitle>
                    <CardDescription>
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {formatDate(season.start_date)} - {formatDate(season.end_date)}
                    </CardDescription>
                  </div>
                  <Badge variant="outline">{season.season_year}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-muted rounded">
                      <div className="font-semibold text-lg">{season.total_games}</div>
                      <div className="text-muted-foreground text-xs">Games</div>
                    </div>
                    <div className="text-center p-2 bg-muted rounded">
                      <div className="font-semibold text-lg">
                        <Users className="w-4 h-4 inline" />
                        {season.total_participants}
                      </div>
                      <div className="text-muted-foreground text-xs">Participants</div>
                    </div>
                    {season.total_revenue > 0 && (
                      <div className="text-center p-2 bg-muted rounded">
                        <div className="font-semibold text-lg">
                          <TrendingUp className="w-4 h-4 inline" />
                          {formatCurrency(season.total_revenue)}
                        </div>
                        <div className="text-muted-foreground text-xs">Revenue</div>
                      </div>
                    )}
                  </div>
                  
                  {season.summary_stats && Object.keys(season.summary_stats).length > 0 && (
                    <div className="pt-3 border-t">
                      <p className="text-xs text-muted-foreground mb-2">Season Highlights</p>
                      <div className="space-y-1 text-xs">
                        {Object.entries(season.summary_stats).slice(0, 3).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-muted-foreground capitalize">
                              {key.replace(/_/g, ' ')}:
                            </span>
                            <span className="font-medium">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <Button variant="outline" size="sm" className="w-full mt-2">
                    View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
